import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FIRM_DASHBOARD_DATA_QUERY_KEY } from 'common/constants/services/firms';
import { useStore } from 'common/store';
import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import { useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import type { DashboardIssues } from 'pages/Intelligence/components/SummaryDashboardEditDialog/dashboardEditFields.types';
import { resolveDashboardSaveErrors } from 'pages/Intelligence/components/SummaryDashboardEditDialog/dashboardSaveErrors';
import type { SummaryDashboardType } from 'services/hooks/summaryDefinitions';
import { getObjectValue } from 'utilities';

interface SaveParams {
  // Omitted from the PATCH when unchanged, so a dashboard-only edit does not touch the name.
  currentName: string;
  definition: DashboardDefinition;
  definitionId: number;
  name: string;
}

interface UseDashboardDefinitionSaveParams {
  summaryType?: SummaryDashboardType;
}

interface UseDashboardDefinitionSaveResult {
  errors: DashboardIssues | null;
  isSaving: boolean;
  resetErrors: () => void;
  // Resolves true when the write landed, so the caller closes the dialog only on success.
  save: (params: SaveParams) => Promise<boolean>;
}

// Sibling of useDashboardGeneration, which owns the identical write → invalidate → refetch sequence
// for the AI path. Kept out of FirmSummaryWorkspace so the workspace does not grow a sixth save
// handler; the firm id comes from the store because the definitions context does not expose its
// entityId.
export const useDashboardDefinitionSave = ({
  summaryType = 'firm',
}: UseDashboardDefinitionSaveParams = {}): UseDashboardDefinitionSaveResult => {
  const [storeValue] = useStore();
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  const firmId = Number(currentFirm.id);

  const { updateDefinition, refetch } = useSummaryDefinitions();
  const queryClient = useQueryClient();

  const [errors, setErrors] = useState<DashboardIssues | null>(null);
  // The context's isSaving covers create, delete and reorder only — never updateDefinition — so
  // every caller owns its own pending state.
  const [isSaving, setIsSaving] = useState(false);

  const resetErrors = useCallback(() => setErrors(null), []);

  const save = useCallback(
    async ({ currentName, definition, definitionId, name }: SaveParams): Promise<boolean> => {
      setErrors(null);
      setIsSaving(true);

      try {
        // One PATCH: the name and the dashboard are independent fields on the same row and the
        // serializer validates both in one pass, so splitting them would only buy a window where
        // the rename landed and the dashboard did not.
        await updateDefinition(definitionId, {
          ...(name === currentName ? {} : { name }),
          dashboard_definition: definition,
        });

        // The definition mutations invalidate the spreadsheet data key, never this one, so without
        // it the edited tiles would render against the previous response until the 5-minute
        // staleTime expired. A partial key matches every (measurementDate, fundId) variant.
        await queryClient.invalidateQueries({
          queryKey: [FIRM_DASHBOARD_DATA_QUERY_KEY, summaryType, firmId, definitionId],
        });
        // updateDefinition writes nothing to the React Query cache, so without this the tab strip
        // keeps the old label and the grid keeps the old components.
        await refetch();

        return true;
      } catch (error) {
        setErrors(resolveDashboardSaveErrors(error));

        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [firmId, queryClient, refetch, summaryType, updateDefinition]
  );

  return { errors, isSaving, resetErrors, save };
};
